import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { applyBlockKind, ZWSP_RE } from './commands';
import { isCellNode, isItemNode } from './schema';

/**
 * Markdown shortcuts typed in Write mode — the prototype's `wkBlockRule` and
 * `wkInlineRule` (C1-editor.md §1.12), ported to ProseMirror's
 * `handleTextInput`: the typed text is inserted first (the prototype reacted
 * to the `input` event, i.e. after insertion), then the rules run on the
 * result inside the same transaction.
 *
 * TipTap's own input rules are NOT used: they fire on different triggers
 * (`---` without Enter, `__bold__`, a `(?:^|\s)` prefix for bold) and skip
 * multi-character input. The NBSP is built from its char code so no tooling
 * can turn it into an invisible literal.
 */

const NBSP = String.fromCharCode(160);
const SP = `[ ${NBSP}]`;
const CALLOUTS = '(?:NOTE|TIP|INFO|WARNING|CAUTION|IMPORTANT)';
// Verbatim port of the prototype marker regex (case-insensitive).
export const MARKER_RE = new RegExp(
    `^(>${SP}?\\[!${CALLOUTS}\\]|\\[!${CALLOUTS}\\]|#{1,3}|[-*+]|\\d+[.)]|>|\\[${SP}?[xX]?\\]|-${SP}?\\[${SP}?\\])${SP}`,
    'i',
);
const SPACE_RE = new RegExp(SP);
const NBSP_RE = new RegExp(NBSP, 'g');
const INLINE_TRIGGER_RE = /[*_~`]/;

// Prototype inline rules, in order (ties on the start index go to the earlier rule).
export const INLINE_RULES = Object.freeze([
    { re: /\*\*([^*\s](?:[^*]*[^*\s])?)\*\*/, mark: 'bold', lead: false },
    { re: /~~([^~\s](?:[^~]*[^~\s])?)~~/, mark: 'strike', lead: false },
    { re: /`([^`]+)`/, mark: 'code', lead: false },
    { re: /(^|[^*\w])\*([^*\s](?:[^*]*[^*\s])?)\*(?!\*)/, mark: 'italic', lead: true },
    { re: /(^|[^_\w])_([^_\s](?:[^_]*[^_\s])?)_(?![_\w])/, mark: 'italic', lead: true },
]);

/** Position after the first `count` text characters of the textblock starting at `start`. */
function posAfterChars(parent, start, count) {
    let pos = start;
    let left = count;
    for (let i = 0; i < parent.childCount && left > 0; i += 1) {
        const child = parent.child(i);
        if (child.isText) {
            const text = child.text;
            let k = 0;
            while (k < text.length && left > 0) {
                if (text[k] !== String.fromCharCode(0x200b)) left -= 1;
                k += 1;
            }
            pos += k;
        } else {
            pos += child.nodeSize;
        }
    }
    return left > 0 ? null : pos;
}

function blockTag($pos) {
    const parent = $pos.parent;
    if (parent.type.name === 'paragraph' && $pos.depth === 1) return 'P';
    if (parent.type.name === 'blockquote' && $pos.depth === 1) return 'BLOCKQUOTE';
    if (isItemNode(parent) && $pos.depth === 2) return 'LI';
    return null;
}

/**
 * The block rule on the transaction's caret block (prototype `wkBlockRule`).
 * `text` is what was just inserted; `bulk` = more than one character
 * (autocorrect, IME commit, dictation, plain-text paste): then the WHOLE block
 * is tested, otherwise the text before the caret must be exactly marker+space.
 */
export function applyBlockRule(tr, text, bulk) {
    const sel = tr.selection;
    if (!sel.empty) return false;
    const $c = sel.$from;
    const tag = blockTag($c);
    if (!tag) return false;
    const parent = $c.parent;
    const start = $c.start();
    const before = tr.doc.textBetween(start, $c.pos, '', '').replace(ZWSP_RE, '');
    const src = bulk ? parent.textContent.replace(ZWSP_RE, '') : before;
    const m = src.match(MARKER_RE);
    if (!m || (!bulk && before !== m[0])) return false;
    const mk = m[1].replace(NBSP_RE, ' ');
    const listType = tag === 'LI' ? $c.node($c.depth - 1).type.name : null;
    let kind = null;
    const co = mk.match(/\[!(\w+)\]$/);
    if (co) {
        if (tag === 'LI') return false;
        if ((tag === 'BLOCKQUOTE' && !/^>/.test(mk)) || tag !== 'BLOCKQUOTE') {
            kind = /WARN|CAUTION|IMPORTANT/i.test(co[1]) ? 'warn' : 'note';
        }
    } else if (tag === 'BLOCKQUOTE') {
        return false;
    } else if (tag === 'LI') {
        if (/^\[ ?[xX]?\]$/.test(mk) && (listType === 'bulletList' || listType === 'taskList')) kind = 'task';
    } else if (/^#{1,3}$/.test(mk)) kind = `h${mk.length}`;
    else if (/^[-*+]$/.test(mk)) kind = 'ul';
    else if (/^\d+[.)]$/.test(mk)) kind = 'ol';
    else if (mk === '>') kind = 'quote';
    else kind = 'task';
    if (!kind) return false;
    const cutEnd = posAfterChars(parent, start, m[0].length);
    if (cutEnd === null) return false;
    tr.delete(start, cutEnd);
    if (tag === 'LI' && kind === 'task' && listType === 'taskList') return true;
    if (tag === 'BLOCKQUOTE') {
        tr.setNodeMarkup($c.before(), null, { callout: kind });
        return true;
    }
    applyBlockKind(tr, kind);
    if (bulk && INLINE_TRIGGER_RE.test(text)) applyInlineRules(tr, true);
    return true;
}

/**
 * Inline rules on the text run just before the caret (prototype
 * `wkInlineRule`). Single-character input: the match must end at the caret.
 * `scanAll` (bulk input): converts up to 40 matches left of the caret.
 * Never inside code blocks, inline code, links or plain-text table cells.
 */
export function applyInlineRules(tr, scanAll) {
    const sel = tr.selection;
    if (!sel.empty) return false;
    const $pos = sel.$from;
    const parent = $pos.parent;
    const { schema } = tr.doc.type;
    if (!parent.isTextblock || parent.type.spec.code || isCellNode(parent)) return false;
    if (!parent.type.allowsMarkType(schema.marks.bold)) return false;
    const nb = $pos.nodeBefore;
    if (!nb || !nb.isText) return false;
    const baseMarks = nb.marks;
    if (baseMarks.some((mk) => mk.type === schema.marks.code || mk.type === schema.marks.link)) return false;
    let runStart = $pos.pos - nb.text.length;
    let caret = $pos.pos;
    let did = false;
    for (let guard = 0; guard < 40; guard += 1) {
        const text = tr.doc.textBetween(runStart, caret, '', '');
        let best = null;
        INLINE_RULES.forEach((rule) => {
            const m = rule.re.exec(text);
            if (!m) return;
            if (!scanAll && m.index + m[0].length !== text.length) return;
            if (!best || m.index < best.m.index) best = { m, rule };
        });
        if (!best) break;
        const lead = best.rule.lead ? best.m[1] : '';
        const inner = best.rule.lead ? best.m[2] : best.m[1];
        const a = runStart + best.m.index + lead.length;
        const b = runStart + best.m.index + best.m[0].length;
        const marks = schema.marks[best.rule.mark].create().addToSet(baseMarks);
        tr.replaceWith(a, b, schema.text(inner, marks));
        caret -= (b - a) - inner.length;
        runStart = a + inner.length;
        did = true;
        if (!scanAll) break;
    }
    if (did) {
        tr.setSelection(TextSelection.create(tr.doc, caret));
        // Typing on continues unformatted (the prototype appended a ZWSP).
        tr.setStoredMarks(baseMarks);
    }
    return did;
}

/**
 * Run the rules for inserted text on a transaction that already contains the
 * insertion. Returns true when a rule changed the transaction.
 */
export function applyTypedRules(tr, text) {
    const bulk = String(text).length > 1;
    let did = false;
    if (SPACE_RE.test(text)) did = applyBlockRule(tr, text, bulk);
    if (!did && INLINE_TRIGGER_RE.test(text)) did = applyInlineRules(tr, bulk);
    return did;
}

export const wikiInputRulesKey = new PluginKey('wikiInputRules');

/** The ProseMirror plugin: `handleTextInput` → insert, run the rules, dispatch only if a rule fired. */
export function createInputRulesPlugin() {
    return new Plugin({
        key: wikiInputRulesKey,
        props: {
            handleTextInput(view, from, to, text) {
                const { state } = view;
                const $from = state.doc.resolve(from);
                const parent = $from.parent;
                if (!parent.isTextblock || parent.type.spec.code || isCellNode(parent)) return false;
                if (!SPACE_RE.test(text) && !INLINE_TRIGGER_RE.test(text)) return false;
                const tr = state.tr.insertText(text, from, to);
                if (!applyTypedRules(tr, text)) return false;
                view.dispatch(tr.scrollIntoView());
                return true;
            },
        },
    });
}
